// Represents a constraint that needs to be validated in a field
export interface Constraint {
  type: string
}

// Represents a set of options to configure how to parse a field
export interface FieldOpts {
  separator?: string
}

// Represents a Field in a Csv import
export interface Field {
  key: string
  type: string
  displayType: string
  label: string
  constraints?: Constraint[]
  options?: FieldOpts
  selectedColumn?: CsvOption | null
}

// Represents a mapping between a Header in a Csv import and a Field
export interface CsvField {
  name: string
  index: number
  mapping: Field | null
  preview?: string
}

export interface CsvOption {
  key: string
  label: string
}

// Represents the result of parsing a Csv file using papaparse
export interface ParseResult {
  data: string[][]
  errors: { message: string }[]
}

// Represents the structure expected in the backend of each field mapping
export interface MappingPayload {
  index: number
  header_name: string
  key: string
  options?: FieldOpts
  type?: string
}

// Represents the whole import payload expected by the backend
export interface ImportPayload {
  csv_file?: File
  mapping: string | MappingPayload[]
  additionalMapping?: MappingPayload[]
  rows: string | string[]
}

/**
 * Builds an array of CsvField objects from parsed CSV data.
 * Takes the first row as headers and the second row as preview data.
 * @param {ParseResult} results - The parsed CSV data containing headers and rows
 * @returns {CsvField[]} An array of CsvField objects with name, index, mapping, and preview properties
 */
export function buildCsvFields(results: ParseResult): CsvField[] {
  const headers = results.data[0]
  const previewRow = results.data[1]

  return headers
    ? headers.map((name: string, index: number) => ({
        name,
        index,
        mapping: null as Field | null,
        preview: previewRow[index],
      }))
    : []
}

/**
 * Builds an import mapping payload from system fields and CSV field mappings.
 * Filters system fields that have a selected column and creates mapping payload objects
 * containing the CSV field information and system field configuration.
 * @param {Field[]} systemFields - Array of system field definitions with potential column selections
 * @param {Record<string, CsvField>} csvFieldsByName - Dictionary mapping field keys to CSV field objects
 * @returns {MappingPayload[]} An array of mapping payload objects for fields that have selected columns
 */
export function buildImportMappingPayload(
  systemFields: Field[],
  csvFieldsByName: Record<string, CsvField>,
): MappingPayload[] {
  const mapping = systemFields.reduce((acc: MappingPayload[], field: Field) => {
    if (field.selectedColumn !== null) {
      const csvField = csvFieldsByName[field.selectedColumn.key]

      if (csvField) {
        acc.push({
          header_name: csvField.name,
          index: csvField.index,
          key: field.key,
          options: field.options,
          type: field.type,
        })
      }
    }
    return acc
  }, [])

  return mapping
}
